// Background prefetch controller for /data/menus/{slug}.json files.
//
// After the restaurants list loads, App.tsx calls startPrefetch(allSlugs) to
// pull every menu in the background. RestaurantCard's expand path calls
// prioritize(slug) — returns the in-flight promise if any, splices a queued
// slug to immediate fetch, or kicks off a fresh fetch (bypassing the worker
// concurrency cap, since priority fetches are user-initiated and rare).
//
// Each fetch lands in useStore.loadedMenus via storeMenu, so RestaurantCard
// renders without re-fetching.
//
// HTTP caching is handled at the Cloudflare Pages layer via web/_headers.
// This controller does not implement IndexedDB or Service Worker — those are
// follow-ups if offline support becomes a goal.

import type { EnrichedRestaurant } from "../../src/types";
import { useStore } from "./store";

const BG_CONCURRENCY = 3;
const RETRY_DELAY_MS = 25;
const RETRY_JITTER_MS = 25;

interface State {
  queue: string[];
  inFlight: Map<string, Promise<EnrichedRestaurant>>;
  controller: AbortController;
  activeWorkers: number;
}

let state: State = freshState();

function freshState(): State {
  return {
    queue: [],
    inFlight: new Map(),
    controller: new AbortController(),
    activeWorkers: 0,
  };
}

function alreadyKnown(slug: string): boolean {
  return useStore.getState().loadedMenus[slug] !== undefined || state.inFlight.has(slug);
}

export function startPrefetch(slugs: string[]): void {
  let added = 0;
  for (const slug of slugs) {
    if (alreadyKnown(slug)) continue;
    if (state.queue.includes(slug)) continue;
    state.queue.push(slug);
    added++;
  }
  if (added > 0) useStore.getState().incrementPrefetchTotal(added);
  pumpWorkers();
}

export function prioritize(slug: string): Promise<EnrichedRestaurant> {
  const loaded = useStore.getState().loadedMenus[slug];
  if (loaded) return Promise.resolve(loaded);

  const inFlight = state.inFlight.get(slug);
  if (inFlight) return inFlight;

  // Splice from background queue (if present) — priority fetches don't wait.
  const i = state.queue.indexOf(slug);
  if (i >= 0) state.queue.splice(i, 1);

  return fetchOne(slug, /* counts as worker */ false);
}

export function cancelAll(): void {
  state.controller.abort();
  state = freshState();
}

function pumpWorkers(): void {
  while (state.activeWorkers < BG_CONCURRENCY && state.queue.length > 0) {
    const slug = state.queue.shift();
    if (!slug) break;
    if (alreadyKnown(slug)) continue;
    state.activeWorkers++;
    // Background workers swallow errors (already logged in doFetch); only the
    // priority path (prioritize) returns the rejection to the caller.
    fetchOne(slug, /* counts as worker */ true)
      .catch(() => {})
      .finally(() => {
        state.activeWorkers--;
        pumpWorkers();
      });
  }
}

function fetchOne(slug: string, _isWorker: boolean): Promise<EnrichedRestaurant> {
  const existing = state.inFlight.get(slug);
  if (existing) return existing;

  const promise = doFetch(slug, /* attempt */ 0);
  state.inFlight.set(slug, promise);

  // Clean up the inFlight registry once the promise settles, regardless of outcome.
  // The .catch(() => {}) prevents the orphan branch from firing an unhandled
  // rejection — the original `promise` is still returned and propagates errors
  // to callers that need them (priority path).
  promise
    .catch(() => {})
    .finally(() => {
      if (state.inFlight.get(slug) === promise) state.inFlight.delete(slug);
    });
  return promise;
}

async function doFetch(slug: string, attempt: number): Promise<EnrichedRestaurant> {
  try {
    const resp = await fetch(`/data/menus/${slug}.json`, { signal: state.controller.signal });
    if (!resp.ok) throw new Error(`status ${resp.status}`);
    const menu = (await resp.json()) as EnrichedRestaurant;
    useStore.getState().storeMenu(slug, menu);
    useStore.getState().incrementPrefetchSettled(1);
    return menu;
  } catch (err) {
    if ((err as Error).name === "AbortError") throw err;
    if (attempt === 0) {
      const delay = RETRY_DELAY_MS + Math.random() * RETRY_JITTER_MS;
      await new Promise<void>((r) => setTimeout(r, delay));
      return doFetch(slug, 1);
    }
    console.warn("menu prefetch failed:", slug, err);
    useStore.getState().incrementPrefetchSettled(1);
    throw err;
  }
}
